import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

export interface IControlledDragAndDropInputProps {
  buttonText: string;
  accept: string;
  multiple?: boolean;
  onFileChange: (files: File[]) => void;
}

const ControlledDragAndDropInput = ({
  buttonText,
  accept,
  multiple = false,
  onFileChange,
}: IControlledDragAndDropInputProps) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(droppedFiles);
    onFileChange(droppedFiles);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setFiles(files);
      onFileChange(files);
    }
  };

  return (
    <div>
      <Paper
        elevation={0}
        className={`${classes.dropzone} ${isDragActive ? classes.dragActive : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <CloudUploadIcon className={classes.icon} />
        <Typography variant="h6">
          {isDragActive ? 'Drop files here' : (buttonText || 'Drag & drop files or click to upload')}
        </Typography>
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          ref={inputRef}
          className={classes.hiddenInput}
          onChange={handleFileChange}
        />
      </Paper>

      {files.length > 0 && (
        <List className={classes.list}>
          {files.map((file, index) => (
            <Typography key={index}>
              {file.name} ({Math.round(file.size / 1024)} KB)
            </Typography>
          ))}
        </List>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  dropzone: {
    border: `1px dashed rgba(255, 14, 175, 0.4)`,
    padding: theme.spacing(4),
    textAlign: 'center',
    backgroundColor: '#fafafa',
    position: 'relative',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  dragActive: {
    backgroundColor: '#e3f2fd',
  },
  icon: {
    fontSize: 48,
    color: theme.palette.primary.main,
  },
  hiddenInput: {
    display: 'none',
  },
  list: {
    marginTop: theme.spacing(2),
  },
}));

export default ControlledDragAndDropInput;